import type { InfiniteData, QueryKey } from '@tanstack/react-query'
import type {
  Product,
  ProductStatusesQueryData,
} from '@skop-app/lib-domain/types'
import type { SearchProductInput } from '@skop-app/lib-search'

import { isRecord } from '@/utils/typeGuards'

export type ProductStatusesRouteHint = 'inventory-active' | 'inventory-removed'

export type ProductStatusesScope = {
  userID?: string
  scopeKind?: 'organization' | 'solo' | 'unresolved'
  personalWorkspaceOrganizationID?: string
  organizationID?: string
  isOrganizationView?: boolean
  isMaster: boolean
  filters?: SearchProductInput
  lang: string
  pageSize?: number
  routeHint?: ProductStatusesRouteHint
  teams?: Array<string | number>
}

export type ProductStatusesQueryKey = readonly [
  'productStatuses',
  ProductStatusesScope?,
]

type ProductStatusesPage = ProductStatusesQueryData['pages'][number]

type ProductStatusesPageLike =
  | (Partial<ProductStatusesPage> & { nextToken?: unknown })
  | undefined
  | null

type ProductStatusesInfiniteLike =
  | ProductStatusesQueryData
  | InfiniteData<{ items?: Product[]; nextToken?: unknown }, unknown>

const normalizeProductStatusesPage = (
  page: ProductStatusesPageLike,
): ProductStatusesPage =>
  ({
    ...(isRecord(page) ? page : {}),
    items: Array.isArray(page?.items) ? page.items : [],
    nextToken: page?.nextToken ?? null,
  }) as ProductStatusesPage

export const createProductStatusesPage = (
  items: Product[] = [],
): ProductStatusesPage => normalizeProductStatusesPage({ items })

export const normalizeProductStatusesPages = (
  pages: ProductStatusesPageLike[] | undefined,
): ProductStatusesPage[] => {
  if (!Array.isArray(pages) || pages.length === 0) {
    return [createProductStatusesPage()]
  }

  return pages.map((page) => normalizeProductStatusesPage(page))
}

export const toProductStatusesQueryData = (
  data?: Partial<ProductStatusesQueryData> | undefined,
): ProductStatusesQueryData =>
  ({
    pages: normalizeProductStatusesPages(data?.pages),
    pageParams: Array.isArray(data?.pageParams) ? data.pageParams : [],
  }) as ProductStatusesQueryData

export const getProductStatusesScope = (
  queryKey: QueryKey | ProductStatusesQueryKey,
): Partial<ProductStatusesScope> => {
  if (!Array.isArray(queryKey) || queryKey[0] !== 'productStatuses') return {}

  const scope = queryKey[1]
  if (!isRecord(scope)) return {}

  return scope as Partial<ProductStatusesScope>
}

export const getProductStatusesFilters = (
  queryKey: QueryKey | ProductStatusesQueryKey,
): SearchProductInput | undefined => {
  const scope = getProductStatusesScope(queryKey)

  return isRecord(scope.filters)
    ? (scope.filters as SearchProductInput)
    : undefined
}

export const matchesProductStatusesDestination = (
  queryKey: QueryKey | ProductStatusesQueryKey,
  productStatusInput: Partial<Product>,
): boolean => {
  const filters = getProductStatusesFilters(queryKey) ?? {}

  const progressMatches =
    !filters.progress?.eq || filters.progress.eq === productStatusInput.progress
  const typeMatches =
    !filters.type ||
    ((filters.type.eq === undefined ||
      filters.type.eq === productStatusInput.type) &&
      (filters.type.ne === undefined ||
        (productStatusInput.type !== undefined &&
          filters.type.ne !== productStatusInput.type)))

  return progressMatches && typeMatches
}

export const replaceProductStatusInQueryData = ({
  previousProducts,
  variables,
}: {
  previousProducts: ProductStatusesQueryData | undefined
  variables: Partial<Product>
}): ProductStatusesQueryData['pages'] => {
  const currentData = toProductStatusesQueryData(previousProducts)

  const matchingPageIndex = currentData.pages.findIndex((page) =>
    page.items.some((item) => item.id === variables.id),
  )

  if (matchingPageIndex === -1) {
    const lastPageIndex = currentData.pages.length - 1

    return currentData.pages.map((page, index) =>
      index === lastPageIndex
        ? {
            ...page,
            items: [...page.items, variables as Product],
          }
        : page,
    )
  }

  return currentData.pages.map((page) => ({
    ...page,
    items: page.items.map((item) =>
      item.id === variables.id ? { ...item, ...variables } : item,
    ),
  }))
}

export const getProductStatusesItems = (
  data: ProductStatusesInfiniteLike | Product[],
): Product[] =>
  Array.isArray(data)
    ? data
    : normalizeProductStatusesPages(data.pages).flatMap((page) => page.items)
